import { ExclamationCircleOutlined } from "@ant-design/icons"
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Flex,
  Form,
  InputNumber,
  Modal,
  Select,
  Skeleton,
  Switch,
  Typography,
  message
} from "antd"
import dayjs, { type Dayjs } from "dayjs"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { CustomTimePicker } from "../../../../components/Date&TimePickers/CustomTimePicker.tsx"
import { useDateFormatter } from "../../../../hooks/use-date-formatter.tsx"
import {
  deleteReportSchedule,
  getReportSchedule,
  updateReportSchedule
} from "../../api/services.ts"
import {
  DEFAULT_SCHEDULE_FORM,
  SCHEDULE_FORMATS,
  SCHEDULE_FREQUENCIES,
  type ScheduleRecipientOption,
  buildSchedulePayload,
  computeNextRunPreview,
  isValidEmail,
  mergeRecipientOptions,
  scheduleSaveErrorMessage,
  scheduleToFormValues,
  usesDayOfMonth,
  usesWeekday
} from "../../helpers/reportSchedule.ts"
import {
  filterShareUserOption,
  useShareCandidateOptions
} from "../../hooks/useReportShareUsers.ts"
import type {
  ReportExportFormat,
  ReportSchedule,
  ReportScheduleFrequency
} from "../../types/collab.types.ts"
import type { ReportOwner } from "../../types/types.ts"

const { Text } = Typography

interface DrawerFormValues {
  enabled: boolean
  frequency: ReportScheduleFrequency
  weekday: number
  day_of_month: number
  send_time: Dayjs
  recipients_users: (ReportOwner | number | string)[]
  recipients_emails: string[]
  formats: ReportExportFormat[]
}

const WEEKDAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
] as const

interface ReportScheduleDrawerProps {
  reportId: string
  onClose: () => void
}

export const ReportScheduleDrawer = ({
  reportId,
  onClose
}: ReportScheduleDrawerProps) => {
  const { t } = useTranslation()
  const { formatDate, timezone } = useDateFormatter()
  const { options: candidateOptions, loading: usersLoading } =
    useShareCandidateOptions(reportId)
  const [form] = Form.useForm<DrawerFormValues>()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [hasSchedule, setHasSchedule] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [storedRecipients, setStoredRecipients] = useState<ReportOwner[]>([])
  const [lastRunAt, setLastRunAt] =
    useState<ReportSchedule["last_run_at"]>(null)

  const recipientOptions = useMemo(
    () =>
      mergeRecipientOptions(
        storedRecipients,
        candidateOptions as ScheduleRecipientOption[]
      ),
    [storedRecipients, candidateOptions]
  )

  const enabled = Form.useWatch("enabled", form) ?? true
  const frequency = Form.useWatch("frequency", form) ?? "weekly"
  const weekday = Form.useWatch("weekday", form)
  const dayOfMonth = Form.useWatch("day_of_month", form)
  const sendTime = Form.useWatch("send_time", form)

  const nextRunPreview = useMemo(() => {
    if (loading || !enabled) return null
    return computeNextRunPreview(
      {
        frequency,
        weekday: weekday ?? null,
        day_of_month: dayOfMonth ?? null,
        send_time: sendTime ? sendTime.format("HH:mm") : ""
      },
      dayjs().tz(timezone)
    )
  }, [loading, enabled, frequency, weekday, dayOfMonth, sendTime, timezone])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const response = await getReportSchedule(reportId)
      if (cancelled) return
      const schedule = response.ok ? (response.data ?? null) : null
      setHasSchedule(Boolean(schedule))
      setStoredRecipients(schedule?.recipients_users ?? [])
      const values = scheduleToFormValues(schedule)
      form.setFieldsValue({
        enabled: values.enabled,
        frequency: values.frequency,
        weekday: values.weekday ?? DEFAULT_SCHEDULE_FORM.weekday!,
        day_of_month:
          values.day_of_month ?? DEFAULT_SCHEDULE_FORM.day_of_month!,
        send_time: dayjs(values.send_time, "HH:mm"),
        recipients_users: values.recipients_users,
        recipients_emails: values.recipients_emails,
        formats: values.formats
      })
      setLastRunAt(schedule?.last_run_at ?? null)
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [reportId, form])

  const handleSave = async () => {
    const values = await form.validateFields()
    setSaving(true)
    setSaveError(null)
    try {
      const response = await updateReportSchedule(
        reportId,
        buildSchedulePayload({
          enabled: values.enabled,
          frequency: values.frequency,
          weekday: values.weekday,
          day_of_month: values.day_of_month,
          send_time: values.send_time.format("HH:mm"),
          recipients_users: values.recipients_users ?? [],
          recipients_emails: values.recipients_emails ?? [],
          formats: values.formats ?? []
        })
      )
      if (response.ok) {
        message.success(t("reports.schedule.saved"))
        onClose()
      } else {
        setSaveError(
          scheduleSaveErrorMessage(response) ?? t("reports.schedule.saveError")
        )
      }
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = () => {
    Modal.confirm({
      title: t("reports.schedule.deleteConfirm.title"),
      content: t("reports.schedule.deleteConfirm.body"),
      icon: <ExclamationCircleOutlined />,
      okText: t("reports.schedule.deleteConfirm.confirm"),
      okButtonProps: { danger: true },
      cancelText: t("reports.cancel"),
      onOk: async () => {
        setDeleting(true)
        try {
          const response = await deleteReportSchedule(reportId)
          if (response.ok) {
            message.success(t("reports.schedule.deleted"))
            onClose()
          } else {
            message.error(t("reports.schedule.deleteError"))
          }
        } finally {
          setDeleting(false)
        }
      }
    })
  }

  const controlsDisabled = !enabled

  return (
    <Drawer
      open
      width={460}
      title={t("reports.schedule.title")}
      onClose={onClose}
      footer={
        <Flex justify="space-between" align="center">
          <Button
            danger
            disabled={!hasSchedule}
            loading={deleting}
            onClick={handleDelete}
          >
            {t("reports.schedule.delete")}
          </Button>
          <Flex gap="small">
            <Button onClick={onClose}>{t("reports.cancel")}</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              {t("reports.schedule.save")}
            </Button>
          </Flex>
        </Flex>
      }
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <Form<DrawerFormValues> form={form} layout="vertical">
          {saveError && (
            <Alert
              type="error"
              showIcon
              className="mb-4"
              message={saveError}
              closable
              onClose={() => setSaveError(null)}
            />
          )}
          <Form.Item
            name="enabled"
            label={t("reports.schedule.enable")}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item name="frequency" label={t("reports.schedule.frequency")}>
            <Select
              disabled={controlsDisabled}
              options={SCHEDULE_FREQUENCIES.map((value) => ({
                label: t(`reports.schedule.frequencies.${value}`),
                value
              }))}
            />
          </Form.Item>

          {usesWeekday(frequency) && (
            <Form.Item
              name="weekday"
              label={t("reports.schedule.weekday")}
              rules={[
                {
                  required: true,
                  message: t("reports.schedule.errors.weekdayRequired")
                }
              ]}
            >
              <Select
                disabled={controlsDisabled}
                options={WEEKDAY_KEYS.map((key, index) => ({
                  label: t(`reports.schedule.weekdays.${key}`),
                  value: index
                }))}
              />
            </Form.Item>
          )}

          {usesDayOfMonth(frequency) && (
            <Form.Item
              name="day_of_month"
              label={t("reports.schedule.dayOfMonth")}
              rules={[
                {
                  required: true,
                  type: "number",
                  min: 1,
                  max: 28,
                  message: t("reports.schedule.errors.dayOfMonthRange")
                }
              ]}
            >
              <InputNumber
                min={1}
                max={28}
                disabled={controlsDisabled}
                style={{ width: "100%" }}
              />
            </Form.Item>
          )}

          <Form.Item
            name="send_time"
            label={t("reports.schedule.sendTime")}
            extra={t("reports.schedule.timezoneHint")}
          >
            <CustomTimePicker
              disabled={controlsDisabled}
              style={{ width: "100%" }}
              allowClear={false}
            />
          </Form.Item>

          <Form.Item
            name="recipients_users"
            label={t("reports.schedule.recipientsUsers")}
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              disabled={controlsDisabled}
              loading={usersLoading}
              maxTagCount="responsive"
              filterOption={filterShareUserOption}
              placeholder={t("reports.schedule.recipientsUsersPlaceholder")}
              options={recipientOptions}
            />
          </Form.Item>

          <Form.Item
            name="recipients_emails"
            label={t("reports.schedule.recipientsEmails")}
            rules={[
              {
                validator: (_rule, value: string[] | undefined) => {
                  const invalid = (value ?? []).find(
                    (email) => !isValidEmail(email)
                  )
                  return invalid
                    ? Promise.reject(
                        new Error(
                          t("reports.schedule.errors.invalidEmail", {
                            email: invalid
                          })
                        )
                      )
                    : Promise.resolve()
                }
              }
            ]}
          >
            <Select
              mode="tags"
              allowClear
              disabled={controlsDisabled}
              tokenSeparators={[",", " ", ";"]}
              notFoundContent={null}
              suffixIcon={null}
              placeholder={t("reports.schedule.recipientsEmailsPlaceholder")}
            />
          </Form.Item>

          <Form.Item
            name="formats"
            label={t("reports.schedule.formats")}
            rules={[
              {
                validator: (_rule, value: ReportExportFormat[] | undefined) => {
                  if (!enabled) return Promise.resolve()
                  return value && value.length > 0
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(t("reports.schedule.errors.formatsRequired"))
                      )
                }
              }
            ]}
          >
            <Checkbox.Group
              options={SCHEDULE_FORMATS.map((value) => ({
                label: t(
                  `reports.export.${value === "xlsx" ? "excel" : value}`
                ),
                value
              }))}
              disabled={controlsDisabled}
            />
          </Form.Item>

          {(nextRunPreview || lastRunAt) && (
            <Flex vertical gap={4} className="mt-2">
              {nextRunPreview && (
                <Text type="secondary" data-testid="schedule-next-run">
                  {t("reports.schedule.nextRun")}:{" "}
                  {formatDate(nextRunPreview.toISOString())}
                </Text>
              )}
              {lastRunAt && (
                <Text type="secondary">
                  {t("reports.schedule.lastRun")}: {formatDate(lastRunAt)}
                </Text>
              )}
            </Flex>
          )}
        </Form>
      )}
    </Drawer>
  )
}
